export default {
  namespaced: true,
  
  state: {
    // 账户
    accounts: [],
    selectedAccount: null,
    
    // 子账号
    subAccounts: [],
    selectedSubAccount: null,
    
    // 归集记录
    collectionRecords: [],
    
    // 盈利明细
    profitDetails: [],

    // 资金损耗明细
    fundLossDetails: [],

    // 每日收益记录列表
    dailyProfitRecords: [],

    // 账户统计列表
    accountStatistics: [],
    
    // 账号统计数据
    accountStats: {
      accountCount: 0,
      subAccountCount: 0,
      totalAssets: 0,
      totalProfit: 0,
      uncollectedFunds: 0,
      fundLoss: 0
    },

    // 子账号统计数据
    subAccountStats: {
      subAccountCount: 0,
      totalAssets: 0,
      totalProfit: 0,
      uncollectedFunds: 0,
      fundLoss: 0
    },
  },
  
  getters: {
    accounts: state => state.accounts,
    selectedAccount: state => state.selectedAccount,
    subAccounts: state => state.subAccounts,
    selectedSubAccount: state => state.selectedSubAccount,
    collectionRecords: state => state.collectionRecords,
    profitDetails: state => state.profitDetails,
    fundLossDetails: state => state.fundLossDetails,
    dailyProfitRecords: state => state.dailyProfitRecords,
    accountStatistics: state => state.accountStatistics,
    
    // 计算属性
    accountsCount: state => state.accounts.length,
    subAccountsCount: state => state.subAccounts.length,
    runningAccountsCount: state => state.accounts.filter(account => account.status === 1).length,
    monitoringAccountsCount: state => state.subAccounts.filter(account => account.status === 1).length,
    runningSubAccountsCount: state => state.subAccounts.filter(account => account.status === 1).length,
    
    totalAssets: state => {
      return state.accounts.reduce((total, account) => total + (account.total_wallet_balance || 0), 0)
    },
    
    totalProfit: state => {
      return state.accounts.reduce((total, account) => total + (account.totalProfit || 0), 0)
    },
    
    uncollectedFunds: state => {
      return state.accounts.reduce((total, account) => total + (account.collect_wallet || 0), 0)
    }
  },
  
  mutations: {
    SET_ACCOUNTS(state, accounts) {
      state.accounts = accounts
    },
    
    SET_SELECTED_ACCOUNT(state, account) {
      state.selectedAccount = account
    },
    
    ADD_ACCOUNT(state, account) {
      state.accounts.push(account)
    },
    
    UPDATE_ACCOUNT(state, updatedAccount) {
      const index = state.accounts.findIndex(account => account.id === updatedAccount.id)
      if (index !== -1) {
        // 合并原有账户和更新的账户数据，只更新传入的字段
        const mergedAccount = { ...state.accounts[index], ...updatedAccount }
        state.accounts.splice(index, 1, mergedAccount)
      }
    },
    
    DELETE_ACCOUNT(state, accountId) {
      state.accounts = state.accounts.filter(account => account.id !== accountId)
    },
    
    SET_SUB_ACCOUNTS(state, subAccounts) {
      state.subAccounts = subAccounts
    },
    
    SET_SELECTED_SUB_ACCOUNT(state, subAccount) {
      state.selectedSubAccount = subAccount
    },
    
    ADD_SUB_ACCOUNT(state, subAccount) {
      state.subAccounts.push(subAccount)
    },
    
    UPDATE_SUB_ACCOUNT(state, updatedSubAccount) {
      const index = state.subAccounts.findIndex(subAccount => subAccount.id === updatedSubAccount.id)
      if (index !== -1) {
        state.subAccounts.splice(index, 1, updatedSubAccount)
      }
    },
    
    DELETE_SUB_ACCOUNT(state, subAccountId) {
      state.subAccounts = state.subAccounts.filter(subAccount => subAccount.id !== subAccountId)
    },
    
    SET_COLLECTION_RECORDS(state, records) {
      state.collectionRecords = records
    },
    
    SET_PROFIT_DETAILS(state, details) {
      state.profitDetails = details
    },

    SET_FUND_LOSS_DETAILS(state, details) {
      state.fundLossDetails = details
    },

    SET_DAILY_PROFIT_RECORDS(state, records) {
      state.dailyProfitRecords = records
    },

    SET_ACCOUNT_STATISTICS(state, statistics) {
      state.accountStatistics = statistics
    },
    
    SET_ACCOUNT_STATS(state, stats) {
      state.accountStats = stats
    },
    
    SET_SUB_ACCOUNT_STATS(state, stats) {
      state.subAccountStats = stats
    },
  },
  
  actions: {
    async fetchAccounts({ commit, state }, params = {}) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.getAccounts(params)
        
        if (data.code !== 200) {
          throw new Error(data.message || '获取账户失败')
        }
        commit('SET_ACCOUNTS', data.data.accounts)
        
        // 更新账号统计数据
        if (data.data.accountCount !== undefined) {
          commit('SET_ACCOUNT_STATS', {
            accountCount: data.data.accountCount || 0,
            subAccountCount: data.data.subAccountCount || 0,
            totalAssets: data.data.totalAssets || 0,
            totalProfit: data.data.totalProfit || 0,
            uncollectedFunds: data.data.uncollectedFunds || 0,
            fundLoss: data.data.fundLoss || 0
          })
        }
        
        if (params.limit && params.limit < 200){
          commit('pagination/SET_PAGINATION', {
            current: data.data.page,
            total: data.data.total
          }, { root: true })
        }
        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },
    
    selectAccount({ commit }, account) {
      commit('SET_SELECTED_ACCOUNT', account)
    },
    
    async addAccount({ commit, state }, accountData) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.addAccount(accountData)
        
        if (data.code !== 200) {
          throw new Error(data.message || '添加账户失败')
        }
        
        commit('ADD_ACCOUNT', data.data.account)
        
        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },
    
    async updateAccount({ commit, state }, accountData) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.updateAccount(accountData.id, accountData)
        
        if (data.code !== 200) {
          throw new Error(data.message || '更新账户失败')
        }
        
        commit('UPDATE_ACCOUNT', data.data.account)
        
        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },
    
    async fetchSubAccounts({ commit, state }, params = {}) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.getSubAccounts(params)
        
        if (data.code !== 200) {
          throw new Error(data.message || '获取子账号失败')
        }
        
        commit('SET_SUB_ACCOUNTS', data.data.subAccounts)
        
        // 更新子账号统计数据
        if (data.data.subAccountCount !== undefined) {
          commit('SET_SUB_ACCOUNT_STATS', {
            subAccountCount: data.data.subAccountCount || 0,
            totalAssets: data.data.totalAssets || 0,
            totalProfit: data.data.totalProfit || 0,
            uncollectedFunds: data.data.uncollectedFunds || 0,
            fundLoss: data.data.fundLoss || 0
          })
        }
        
        commit('pagination/SET_PAGINATION', {
          current: data.data.page,
          total: data.data.total
        }, { root: true })
        
        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },
    
    selectSubAccount({ commit }, subAccount) {
      commit('SET_SELECTED_SUB_ACCOUNT', subAccount)
    },
    
    async updateSubAccountApi({ commit, state }, { subAccountId, apiData }) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.updateSubAccountApi(subAccountId, apiData)
        if (data.code !== 200) {
          throw new Error(data.message || '更新子账号API失败')
        }
        commit('UPDATE_SUB_ACCOUNT', data.data.subAccount)
        
        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },
    
    async fetchCollectionRecords({ commit, state }, params = {}) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.getCollectionRecords(params)
        
        if (data.code !== 200) {
          throw new Error(data.message || '获取收款记录失败')
        }
        commit('SET_COLLECTION_RECORDS', data.data.records)
        commit('pagination/SET_PAGINATION', {
          current: data.data.page,
          total: data.data.total
        }, { root: true })
        
        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },
    
    async fetchProfitDetails({ commit, state }, params = {}) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.getProfitDetails(params)
        if (data.code !== 200) {
          throw new Error(data.message || '获取利润详情失败')
        }
        commit('SET_PROFIT_DETAILS', data.data.details)
        commit('pagination/SET_PAGINATION', {
          current: data.data.page,
          total: data.data.total
        }, { root: true })
        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },
    
    // 获取收益趋势
    async getProfitTrend({ commit }) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.getProfitTrend()
        
        if (data.code !== 200) {
          throw new Error(data.message || '获取收益趋势失败')
        }
        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },

    async fetchFundLossDetails({ commit, state }, params = {}) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.getFundLossDetails(params)
        if (data.code !== 200) {
          throw new Error(data.message || '获取资金损耗详情失败')
        }
        commit('SET_FUND_LOSS_DETAILS', data.data.details)
        commit('pagination/SET_PAGINATION', {
          current: data.data.page,
          total: data.data.total
        }, { root: true })
        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },


    
    // 获取仪表盘数据
    async getDashboardData({ commit }) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.getDashboardData()
        if (data.code !== 200) {
          throw new Error(data.message || '获取仪表盘数据失败')
        }
        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },

    // 获取每日收益记录
    async fetchDailyProfitRecords({ commit }, params = {}) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.getDailyProfitRecords(params)

        if (data.code !== 200) {
          throw new Error(data.message || '获取每日收益记录失败')
        }

        commit('SET_DAILY_PROFIT_RECORDS', data.data.records)
        commit('pagination/SET_PAGINATION', {
          current: data.data.page,
          limit: data.data.limit,
          total: data.data.total
        }, { root: true })

        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    },

    // 获取账户统计数据
    async fetchAccountStatistics({ commit }, params = {}) {
      try {
        commit('SET_LOADING', true, { root: true })
        // 使用API模块
        const accountApi = await import('@/api/account').then(m => m.default)
        const data = await accountApi.getAccountStatistics(params)

        if (data.code !== 200) {
          throw new Error(data.message || '获取账户统计失败')
        }

        commit('SET_ACCOUNT_STATISTICS', data.data.records)
        commit('pagination/SET_PAGINATION', {
          current: data.data.page,
          limit: data.data.limit,
          total: data.data.total
        }, { root: true })

        return data
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADING', false, { root: true })
      }
    }
  }
}
